import { useCallback, useMemo } from "react";
import { POPUP_SECTIONS } from "../../../../constant/constant";
import { isSectionVisible, isSectionVisibleForViewport } from "../../../../../shared/section-visibility.js";
import {
  getProductRecommendationLayoutOptionsFromPopup,
} from "../../../../../shared/product-recommendation-popup-schema.js";
import {
  COVER_IMAGE_LAYOUT,
  getCoverImageCatalogSizes,
  getCoverImageDisplay,
} from "../../../../../shared/product-block-display.js";
import {
  ensureProductPageCarouselBuyNowButton,
  stripBuyNowCtaFromProductSection,
} from "../../../../../shared/product-page-upsell-template.js";
import { productHasSelectableVariants } from "../../../../layouts/product-recommendation/utils/product-variant-utils";
import { renderCatalogSubBlock } from "./render-catalog-sub-block.jsx";
import shared from "./catalog-shared.module.css";

export default function useCatalogProductSlots({
  section,
  productData,
  productIndex,
  popupData,
  popupState,
  setPopupState,
  activeStep,
  handleClose,
  isMobile,
  isPreviewMode,
  selectedSubType,
  selectedSubIndex,
  onSubBlockClick,
  showVariantPickerRow = false,
  layoutProfile = "recommendation",
  hideProductButton = false,
  separateCtaColumn = false,
  isHorizontal = false,
  mobileCardDensity = "single",
  surface = "cart-drawer",
}) {
  const subsections = section?.sections || [];
  const isAiUpsellProfile = layoutProfile === "ai-upsell";
  const isCartDrawerHorizontalProfile =
    layoutProfile === "cart-drawer-horizontal" ||
    layoutProfile === "cart-drawer-checkbox-stack" ||
    layoutProfile === "cart-drawer-fbt-bundle";
  const isCartDrawerVerticalProfile = layoutProfile === "cart-drawer-vertical";
  const isCartDrawerProfile =
    isCartDrawerHorizontalProfile || isCartDrawerVerticalProfile;
  const isProductPageSurface = surface === "product-page";
  const isProductPageCartDrawerImage =
    isProductPageSurface && isCartDrawerProfile;
  /** Full-bleed square image on carousel cards (cart drawer + product page). */
  const isFlushVerticalCarouselImage = isCartDrawerVerticalProfile;

  // Seed Buy Now on product-page Carousel only; strip it from cart drawer upsell.
  if (isCartDrawerVerticalProfile && section) {
    if (isProductPageSurface) {
      ensureProductPageCarouselBuyNowButton(section);
    } else {
      stripBuyNowCtaFromProductSection(section);
    }
  }

  const coverImageEntry = useMemo(() => {
    const index = subsections.findIndex((s) => s?.type === POPUP_SECTIONS.COVER_IMAGE);
    if (index < 0) return null;
    return { section: subsections[index], index };
  }, [subsections]);

  const coverImageLayout = useMemo(() => {
    if (!coverImageEntry?.section) return COVER_IMAGE_LAYOUT.SINGLE;
    return getCoverImageDisplay(coverImageEntry.section).layout;
  }, [coverImageEntry?.section]);

  const productImageCount = useMemo(() => {
    const images = productData?.images;
    return Array.isArray(images) ? images.length : 0;
  }, [productData?.images]);

  const useCatalogGallerySlot =
    !isHorizontal &&
    (coverImageLayout === COVER_IMAGE_LAYOUT.HERO_THUMBNAILS ||
      coverImageLayout === COVER_IMAGE_LAYOUT.HORIZONTAL_SLIDER);

  const catalogImageSizes = useMemo(() => {
    if (!coverImageEntry?.section) return null;
    return getCoverImageCatalogSizes(coverImageEntry.section, isMobile);
  }, [coverImageEntry?.section, isMobile]);

  const catalogImageSlotHeight = useMemo(() => {
    if (!coverImageEntry?.section) return null;
    if (isProductPageCartDrawerImage || isFlushVerticalCarouselImage) {
      return null;
    }
    if (isCartDrawerHorizontalProfile) {
      return isMobile ? 64 : 72;
    }
    if (isHorizontal) {
      // AI upsell: fixed square thumb — never trust mismatched saved mainHeight/columnWidth.
      if (isAiUpsellProfile) {
        return isMobile ? 112 : 140;
      }
      return isMobile ? 88 : 104;
    }
    // Vertical recommendation cards: let layout CSS own the square image slot
    // (fixed px heights crop product images and break border alignment).
    return null;
  }, [
    coverImageEntry?.section,
    isMobile,
    coverImageLayout,
    isHorizontal,
    isAiUpsellProfile,
    catalogImageSizes?.mainHeight,
    isCartDrawerHorizontalProfile,
    isCartDrawerVerticalProfile,
    isProductPageCartDrawerImage,
    isFlushVerticalCarouselImage,
  ]);

  const showCatalogImageGallery =
    useCatalogGallerySlot && productImageCount > 1;

  const imageWrapStyle = useMemo(() => {
    if (!catalogImageSlotHeight) return undefined;
    if (isAiUpsellProfile && isHorizontal) {
      return {
        width: "100%",
        height: catalogImageSlotHeight,
        minHeight: catalogImageSlotHeight,
        maxHeight: catalogImageSlotHeight,
        aspectRatio: "1 / 1",
        overflow: "hidden",
        borderRadius: "var(--ai-upsell-border-radius, 14px)",
        flexShrink: 0,
      };
    }
    return {
      height: catalogImageSlotHeight,
      minHeight: catalogImageSlotHeight,
      maxHeight: catalogImageSlotHeight,
    };
  }, [catalogImageSlotHeight, isAiUpsellProfile, isHorizontal]);

  const forceCompactCoverImage =
    !isAiUpsellProfile &&
    (isHorizontal ||
      (isMobile &&
        coverImageLayout === COVER_IMAGE_LAYOUT.SINGLE &&
        productImageCount <= 1));

  const cardShellStyle = useMemo(() => {
    if (isAiUpsellProfile) {
      return { background: "transparent", border: "none" };
    }

    const cardSetting = section?.setting || {};
    const hasMerchantUpsellChrome =
      cardSetting.upsellCardBackground != null ||
      cardSetting.upsellCardBorderColor != null ||
      cardSetting.upsellCardBorderWidth != null;

    if (isCartDrawerProfile || hasMerchantUpsellChrome) {
      const background =
        cardSetting.upsellCardBackground || "#ffffff";
      const borderColor =
        cardSetting.upsellCardBorderColor || "#e5e7eb";
      const borderWidthRaw = cardSetting.upsellCardBorderWidth;
      const borderWidth =
        borderWidthRaw == null || borderWidthRaw === ""
          ? 1
          : Number(borderWidthRaw) || 0;
      return {
        background,
        backgroundColor: background,
        border:
          borderWidth > 0 ? `${borderWidth}px solid ${borderColor}` : "none",
        ["--weu-upsell-card-bg"]: background,
        ["--weu-upsell-card-border-color"]: borderColor,
        ["--weu-upsell-card-border-width"]: `${borderWidth}px`,
      };
    }

    const border = getProductRecommendationLayoutOptionsFromPopup(popupData)
      ?.productCardBorder;
    if (!border?.enabled) {
      return { background: "transparent", border: "none" };
    }
    return {
      background: "transparent",
      border: `${border.width ?? 1}px solid ${border.color ?? "#d4c4b0"}`,
      borderRadius: `${border.radius ?? 10}px`,
      overflow: "hidden",
    };
  }, [popupData, isAiUpsellProfile, isCartDrawerProfile, section?.setting]);

  const imageColStyle = useMemo(() => {
    if (!isHorizontal) return undefined;
    // AI upsell: lock column to the same square as the image wrap.
    if (isAiUpsellProfile && catalogImageSlotHeight) {
      return {
        width: catalogImageSlotHeight,
        minWidth: catalogImageSlotHeight,
        maxWidth: catalogImageSlotHeight,
        flexShrink: 0,
      };
    }
    if (!catalogImageSizes?.columnWidth) return undefined;
    const width = catalogImageSizes.columnWidth;
    return {
      width,
      minWidth: width,
      maxWidth: width,
      flexShrink: 0,
    };
  }, [
    isHorizontal,
    isAiUpsellProfile,
    catalogImageSlotHeight,
    catalogImageSizes?.columnWidth,
  ]);

  const getSection = useCallback(
    (type) => {
      const index = subsections.findIndex((s) => s?.type === type);
      if (index < 0) return null;
      return { section: subsections[index], index };
    },
    [subsections]
  );

  const wrapSlot = useCallback(
    (type, node, extraClass = "") => {
      if (!node) return null;
      const entry = getSection(type);
      if (!entry) return null;
      const isSelected =
        isPreviewMode &&
        selectedSubType === type &&
        selectedSubIndex === entry.index;
      const isCtaSlot = type === POPUP_SECTIONS.BUTTON;
      const isHoverImageZone =
        type === POPUP_SECTIONS.COVER_IMAGE || type === POPUP_SECTIONS.TITLE;
      return (
        <div
          className={`${extraClass} ${isSelected ? shared.subSelected : ""}`.trim()}
          {...(isCtaSlot ? { "data-catalog-cta": "" } : {})}
          {...(isHoverImageZone ? { "data-catalog-hover-images-zone": "" } : {})}
          onClick={(e) => {
            e.stopPropagation();
            onSubBlockClick?.(type, entry.index, productIndex);
          }}
        >
          {node}
        </div>
      );
    },
    [
      getSection,
      isPreviewMode,
      onSubBlockClick,
      productIndex,
      selectedSubIndex,
      selectedSubType,
    ]
  );

  const aiSlotClass = useCallback(
    (type) => {
      if (!isAiUpsellProfile) return "";
      switch (type) {
        case POPUP_SECTIONS.RATING:
          return shared.aiUpsellRatingSlot;
        case POPUP_SECTIONS.PRICE:
          return shared.aiUpsellPriceSlot;
        case POPUP_SECTIONS.VARIANT_PICKER:
          return shared.aiUpsellVariantSlot;
        case POPUP_SECTIONS.QUANTITY_PICKER:
          return shared.aiUpsellQtySlot;
        case POPUP_SECTIONS.DESCRIPTION:
          return shared.aiUpsellDescriptionSlot;
        default:
          return "";
      }
    },
    [isAiUpsellProfile]
  );

  const renderIfVisible = useCallback(
    (type, className = "") => {
      const entry = getSection(type);
      if (!entry) return null;
      const { section: sub, index } = entry;
      if (!isSectionVisible(sub) || !isSectionVisibleForViewport(sub, isMobile)) {
        return null;
      }
      const isCover = type === POPUP_SECTIONS.COVER_IMAGE;
      if (isCover && isAiUpsellProfile && sub) {
        sub.objectFit = "cover";
        sub.setting = sub.setting || {};
        sub.setting.display = {
          ...(sub.setting.display || {}),
          layout: COVER_IMAGE_LAYOUT.SINGLE,
          autoImageHeight: false,
        };
      }
      const slotClass = [className, aiSlotClass(type)].filter(Boolean).join(" ");
      return wrapSlot(
        type,
        renderCatalogSubBlock({
          type,
          productData,
          productIndex,
          productSection: sub,
          productSectionIndex: index,
          popupData,
          popupState,
          setPopupState,
          activeStep,
          handleClose,
          isMobile,
          forceCompactCoverImage:
            isCover &&
            (isAiUpsellProfile ||
              isProductPageCartDrawerImage ||
              isFlushVerticalCarouselImage ||
              !isHorizontal)
              ? false
              : forceCompactCoverImage,
          fixedCatalogSlot: isCover
            ? isProductPageCartDrawerImage || isFlushVerticalCarouselImage
              ? true
              : isAiUpsellProfile
                ? false
                : // Vertical recommendation: fill parent (height 100%), no fixed 132/134px
                  isHorizontal
            : true,
          preferCatalogHeroFrame: false,
          fillParentSlot:
            isCover &&
            !isCartDrawerProfile &&
            (isAiUpsellProfile || !isHorizontal),
          catalogLineClamp:
            type === POPUP_SECTIONS.TITLE &&
            (isCartDrawerVerticalProfile ||
              isCartDrawerHorizontalProfile ||
              !isHorizontal)
              ? 2
              : undefined,
          catalogCompactPrice:
            (isCartDrawerVerticalProfile ||
              (isProductPageSurface && isCartDrawerHorizontalProfile)) &&
            type === POPUP_SECTIONS.PRICE,
          catalogCompactAdded: isCartDrawerProfile && type === POPUP_SECTIONS.BUTTON,
          surface,
        }),
        slotClass
      );
    },
    [
      activeStep,
      aiSlotClass,
      forceCompactCoverImage,
      getSection,
      handleClose,
      isAiUpsellProfile,
      isCartDrawerVerticalProfile,
      isCartDrawerHorizontalProfile,
      isCartDrawerProfile,
      isFlushVerticalCarouselImage,
      isHorizontal,
      isProductPageCartDrawerImage,
      isProductPageSurface,
      isMobile,
      popupData,
      popupState,
      productData,
      productIndex,
      setPopupState,
      surface,
      wrapSlot,
    ]
  );

  const imageWrapClass = [
    isAiUpsellProfile && isHorizontal ? shared.imageWrapAi : shared.imageWrap,
    showCatalogImageGallery ? shared.imageWrapGallery : "",
    isProductPageCartDrawerImage ||
    isFlushVerticalCarouselImage ||
    (!isHorizontal && !isAiUpsellProfile)
      ? shared.imageWrapProductPage
      : "",
  ]
    .filter(Boolean)
    .join(" ");

  const imageBlock = (
    <div className={imageWrapClass} style={imageWrapStyle}>
      {renderIfVisible(POPUP_SECTIONS.COVER_IMAGE)}
    </div>
  );

  const cartDrawerTitleSlotClass =
    isCartDrawerVerticalProfile || !isHorizontal
      ? shared.cartDrawerVerticalTitle
      : "";
  const cartDrawerPriceSlotClass = isCartDrawerVerticalProfile
    ? shared.cartDrawerVerticalPrice
    : "";

  const metaBlock = (
    <div className={isAiUpsellProfile ? shared.textMetaAi : shared.textMeta}>
      {renderIfVisible(POPUP_SECTIONS.TITLE, cartDrawerTitleSlotClass)}
      {renderIfVisible(POPUP_SECTIONS.RATING)}
      {renderIfVisible(POPUP_SECTIONS.PRICE, cartDrawerPriceSlotClass)}
    </div>
  );

  const renderVariantSlot = () => {
    if (!showVariantPickerRow) return null;
    const entry = getSection(POPUP_SECTIONS.VARIANT_PICKER) || {
      section: {
        type: POPUP_SECTIONS.VARIANT_PICKER,
        setting: {
          // Cart drawer horizontal row needs a compact select — not the
          // default recommendation slider (which collapses to empty here).
          display: {
            label: "",
            variantPickerVariant: "dropdown",
          },
        },
      },
      index: "catalog-auto-variant",
    };
    const { section: sub, index } = entry;
    if (!isSectionVisible(sub) || !isSectionVisibleForViewport(sub, isMobile)) {
      return null;
    }
    const node = renderCatalogSubBlock({
      type: POPUP_SECTIONS.VARIANT_PICKER,
      productData,
      productIndex,
      productSection: sub,
      productSectionIndex: index,
      popupData,
      popupState,
      setPopupState,
      activeStep,
      handleClose,
      isMobile,
      surface,
    });
    if (!node) return null;
    const needsAlignmentPlaceholder = !productHasSelectableVariants(productData);
    return (
      wrapSlot(
        POPUP_SECTIONS.VARIANT_PICKER,
        node,
        [shared.variantSlot, aiSlotClass(POPUP_SECTIONS.VARIANT_PICKER)].filter(Boolean).join(" ")
      ) ?? (
        <div
          className={shared.variantSlot}
          data-variant-alignment={needsAlignmentPlaceholder ? "true" : undefined}
        >
          {node}
        </div>
      )
    );
  };

  const ctaSlotClass = [
    separateCtaColumn && isCartDrawerHorizontalProfile
      ? shared.ctaInline
      : isCartDrawerVerticalProfile
        ? shared.ctaCartDrawerVertical
        : shared.cta,
  ]
    .filter(Boolean)
    .join(" ");

  const renderCtaButtons = (className = "") => {
    if (hideProductButton) return null;

    const buttonEntries = subsections
      .map((section, index) => ({ section, index }))
      .filter(({ section }) => section?.type === POPUP_SECTIONS.BUTTON)
      .filter(
        ({ section }) =>
          isSectionVisible(section) &&
          isSectionVisibleForViewport(section, isMobile)
      );

    if (!buttonEntries.length) return null;

    const useButtonRow =
      isCartDrawerVerticalProfile && buttonEntries.length > 1;
    const groupClass = useButtonRow
      ? shared.ctaCartDrawerVerticalGroup
      : className;

    return (
      <div
        className={groupClass}
        data-catalog-cta-group=""
        data-cta-count={buttonEntries.length}
      >
        {buttonEntries.map(({ section, index }) => {
          const node = renderCatalogSubBlock({
            type: POPUP_SECTIONS.BUTTON,
            productData,
            productIndex,
            productSection: section,
            productSectionIndex: index,
            popupData,
            popupState,
            setPopupState,
            activeStep,
            handleClose,
            isMobile,
            catalogCompactAdded: isCartDrawerProfile,
            surface,
          });
          if (!node) return null;

          const isSelected =
            isPreviewMode &&
            selectedSubType === POPUP_SECTIONS.BUTTON &&
            selectedSubIndex === index;

          return (
            <div
              key={`cta-${productIndex}-${index}`}
              className={`${
                useButtonRow ? shared.ctaCartDrawerVerticalItem : className
              } ${isSelected ? shared.subSelected : ""}`.trim()}
              data-catalog-cta=""
              onClick={(e) => {
                e.stopPropagation();
                onSubBlockClick?.(POPUP_SECTIONS.BUTTON, index, productIndex);
              }}
            >
              {node}
            </div>
          );
        })}
      </div>
    );
  };

  const controlsBlock = (
    <div
      className={`${shared.controls} ${
        isAiUpsellProfile ? shared.controlsAi : ""
      }`.trim()}
      data-catalog-controls=""
    >
      {renderVariantSlot()}
      {renderIfVisible(POPUP_SECTIONS.QUANTITY_PICKER)}
      {!separateCtaColumn ? renderCtaButtons(ctaSlotClass) : null}
    </div>
  );

  const ctaBlock = separateCtaColumn ? renderCtaButtons(ctaSlotClass) : null;

  const descriptionBlock = renderIfVisible(
    POPUP_SECTIONS.DESCRIPTION,
    shared.descriptionSlot
  );

  const isTwinMobileCard = isMobile && mobileCardDensity === "twin";

  return {
    imageBlock,
    metaBlock,
    controlsBlock,
    ctaBlock,
    descriptionBlock,
    cardShellStyle,
    imageColStyle,
    isTwinMobileCard,
    isAiUpsellProfile,
  };
}
